import { useCallback, useMemo } from "react";

import {
  getNonEmptyLocaleFields,
  hasAnyLocaleField,
  LocaleFieldKey,
  LocaleFields,
  Locales,
  localeToLocaleFieldKey,
  trimLocaleFields,
} from "@simplyk/common";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { trpc } from "@/common/helpers/trpc";
import { TicketingOutput } from "@/common/types/trpc";
import { useStableLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";

export type RateCategory = NonNullable<TicketingOutput["rateCategories"]>[number];
export type RateCategoryField = RateCategory["fields"][number];

export const getRateCategoryName = (category: RateCategory, locale: Locales): string => {
  const localized = category.fields.find((field) => field.locale === locale);
  if (localized) {
    return localized.name;
  }
  const fallback = category.fields[0];
  return fallback?.name ?? "";
};

export const localeToCategoryFieldKey = (locale: Locales): LocaleFieldKey => localeToLocaleFieldKey[locale];

interface CreateCategoryResult {
  category?: RateCategory;
  errorMessage?: string;
}

interface RenameCategoryResult {
  category?: RateCategory;
  errorMessage?: string;
}

interface DeleteCategoryResult {
  deletedId?: string;
  errorMessage?: string;
}

interface UseRateCategoriesReturn {
  categories: RateCategory[];
  ticketingId: string;
  locale: Locales;
  getCategoryName: (category: RateCategory) => string;
  getCategoryById: (id: string) => RateCategory | undefined;
  createCategory: (input: LocaleFields) => Promise<CreateCategoryResult>;
  renameCategory: (params: { rateCategoryId: string; fields: LocaleFields }) => Promise<RenameCategoryResult>;
  deleteCategory: (rateCategoryId: string) => Promise<DeleteCategoryResult>;
  isCreating: boolean;
  isRenaming: boolean;
  isDeleting: boolean;
}

export const useRateCategories = (): UseRateCategoriesReturn => {
  const { apiFormObject: apiTicketing, updateApiFormObject } = useStableLiveFormEditorContext<"ticketing">();
  const { locale } = useLocaleContext();

  const categories = useMemo(
    () => apiTicketing.rateCategories?.filter((category) => !category.isArchived) ?? [],
    [apiTicketing.rateCategories]
  );

  const ticketingId = apiTicketing.id;

  const { mutateAsync: createMutate, isPending: isCreating } = trpc.createRateCategory.useMutation();
  const { mutateAsync: renameMutate, isPending: isRenaming } = trpc.renameRateCategory.useMutation();
  const { mutateAsync: deleteMutate, isPending: isDeleting } = trpc.deleteRateCategory.useMutation();

  const getCategoryName = useCallback((category: RateCategory) => getRateCategoryName(category, locale), [locale]);

  const getCategoryById = useCallback((id: string) => categories.find((category) => category.id === id), [categories]);

  const writeCategories = useCallback(
    (nextCategories: RateCategory[]) => {
      updateApiFormObject({ ...apiTicketing, rateCategories: nextCategories });
    },
    [apiTicketing, updateApiFormObject]
  );

  const createCategory = useCallback(
    async (input: LocaleFields): Promise<CreateCategoryResult> => {
      const fields = getNonEmptyLocaleFields(input);
      if (!hasAnyLocaleField(fields)) {
        return { errorMessage: "" };
      }
      const { data, error } = await createMutate({ ticketingId, fields });
      if (error || !data) {
        return { errorMessage: error?.message ?? "" };
      }
      writeCategories([...(apiTicketing.rateCategories ?? []), data]);
      return { category: data };
    },
    [apiTicketing.rateCategories, createMutate, ticketingId, writeCategories]
  );

  const renameCategory = useCallback(
    async ({
      rateCategoryId,
      fields: rawFields,
    }: {
      rateCategoryId: string;
      fields: LocaleFields;
    }): Promise<RenameCategoryResult> => {
      const fields = trimLocaleFields(rawFields);
      if (!hasAnyLocaleField(fields)) {
        return { errorMessage: "" };
      }
      const { data, error } = await renameMutate({ rateCategoryId, fields });
      if (error || !data) {
        return { errorMessage: error?.message ?? "" };
      }
      const updated = (apiTicketing.rateCategories ?? []).map((category) =>
        category.id === rateCategoryId ? data : category
      );
      writeCategories(updated);
      return { category: data };
    },
    [apiTicketing.rateCategories, renameMutate, writeCategories]
  );

  const deleteCategory = useCallback(
    async (rateCategoryId: string): Promise<DeleteCategoryResult> => {
      const { data, error } = await deleteMutate({ rateCategoryId });
      if (error || !data) {
        return { errorMessage: error?.message ?? "" };
      }
      const remaining = (apiTicketing.rateCategories ?? []).filter((category) => category.id !== rateCategoryId);
      writeCategories(remaining);
      return { deletedId: data.id };
    },
    [apiTicketing.rateCategories, deleteMutate, writeCategories]
  );

  return {
    categories,
    ticketingId,
    locale,
    getCategoryName,
    getCategoryById,
    createCategory,
    renameCategory,
    deleteCategory,
    isCreating,
    isRenaming,
    isDeleting,
  };
};
